import { OBSTACLE, PLAYER, WORLD } from "./constants";
import type { Obstacle, PlayerState, PowerUp, Projectile } from "./engine/types";

/**
 * Pure draw routines. Keeps no game state — re-renders from the engine
 * snapshot every frame. Backgrounds are layered for parallax.
 */
export class Renderer {
  private readonly ctx: CanvasRenderingContext2D;
  private parallax = { far: 0, near: 0, ground: 0 };

  constructor(ctx: CanvasRenderingContext2D) {
    this.ctx = ctx;
  }

  updateParallax(speedPxPerSec: number, dtSec: number): void {
    this.parallax.far = (this.parallax.far + speedPxPerSec * 0.12 * dtSec) % WORLD.WIDTH;
    this.parallax.near = (this.parallax.near + speedPxPerSec * 0.35 * dtSec) % WORLD.WIDTH;
    this.parallax.ground = (this.parallax.ground + speedPxPerSec * dtSec) % 80;
  }

  drawWorld(opts: {
    player: PlayerState;
    obstacles: Obstacle[];
    powerups: PowerUp[];
    projectiles: Projectile[];
    nowMs: number;
    paused?: boolean;
  }): void {
    const { ctx } = this;
    ctx.save();
    this.drawSky();
    this.drawSkyline();
    this.drawGround();
    this.drawObstacles(opts.obstacles);
    this.drawPowerUps(opts.powerups, opts.nowMs);
    this.drawProjectiles(opts.projectiles);
    this.drawPlayer(opts.player, opts.nowMs);
    if (opts.paused) this.drawPauseOverlay();
    ctx.restore();
  }

  // ── Layers ────────────────────────────────────────────────────────────────
  private drawSky(): void {
    const { ctx } = this;
    const grad = ctx.createLinearGradient(0, 0, 0, WORLD.HEIGHT);
    grad.addColorStop(0, "#0a3161");
    grad.addColorStop(0.6, "#1e1b4b");
    grad.addColorStop(1, "#06122b");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, WORLD.WIDTH, WORLD.HEIGHT);

    // Stars
    ctx.fillStyle = "rgba(255,255,255,0.6)";
    for (let i = 0; i < 60; i++) {
      const x = (i * 173.31 + this.parallax.far * 0.2) % WORLD.WIDTH;
      const y = (i * 91.7) % (WORLD.HEIGHT * 0.55);
      const r = i % 7 === 0 ? 1.6 : 0.9;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  private drawSkyline(): void {
    const { ctx } = this;
    ctx.save();
    ctx.fillStyle = "rgba(10, 20, 50, 0.75)";
    const baseY = WORLD.GROUND_Y - 20;
    const offset = -this.parallax.near;
    for (let i = -1; i < 12; i++) {
      const x = offset + i * 140;
      const h = 80 + ((i * 37) % 110);
      ctx.fillRect(x, baseY - h, 110, h);
      ctx.fillStyle = "rgba(248,249,250,0.05)";
      for (let wx = 0; wx < 90; wx += 14) {
        for (let wy = 0; wy < h - 20; wy += 18) {
          ctx.fillRect(x + 8 + wx, baseY - h + 12 + wy, 6, 8);
        }
      }
      ctx.fillStyle = "rgba(10, 20, 50, 0.75)";
    }
    ctx.restore();
  }

  private drawGround(): void {
    const { ctx } = this;
    ctx.fillStyle = "#0d1b2a";
    ctx.fillRect(0, WORLD.GROUND_Y, WORLD.WIDTH, WORLD.HEIGHT - WORLD.GROUND_Y);
    ctx.strokeStyle = "#c8102e";
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(0, WORLD.GROUND_Y);
    ctx.lineTo(WORLD.WIDTH, WORLD.GROUND_Y);
    ctx.stroke();
    ctx.fillStyle = "rgba(240,179,35,0.8)";
    const dashWidth = 40;
    const gap = 40;
    let x = -this.parallax.ground;
    while (x < WORLD.WIDTH) {
      ctx.fillRect(x, WORLD.GROUND_Y + 18, dashWidth, 3);
      x += dashWidth + gap;
    }
  }

  private drawObstacles(obstacles: Obstacle[]): void {
    const { ctx } = this;
    for (const o of obstacles) {
      const c = OBSTACLE[o.kind].color;
      ctx.fillStyle = c;
      roundedRect(ctx, o.x, o.y, o.w, o.h, 6);
      ctx.fill();
      // Hazard accent
      ctx.fillStyle = "rgba(255,255,255,0.18)";
      ctx.fillRect(o.x + 4, o.y + 4, Math.max(4, o.w - 8), 3);

      if (o.kind === "FLY") {
        ctx.fillStyle = "#fcd34d";
        ctx.beginPath();
        ctx.arc(o.x + o.w * 0.75, o.y + o.h * 0.45, 5, 0, Math.PI * 2);
        ctx.fill();
      } else if (o.kind === "DUCK") {
        ctx.fillStyle = "#fbbf24";
        ctx.fillRect(o.x - 6, o.y - 12, o.w + 12, 6);
      }
    }
  }

  private drawPowerUps(powerups: PowerUp[], nowMs: number): void {
    const { ctx } = this;
    for (const p of powerups) {
      const bob = Math.sin(nowMs / 220 + p.bobPhase) * 6;
      const cx = p.x + p.w / 2;
      const cy = p.y + p.h / 2 + bob;
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(((nowMs % 2000) / 2000) * Math.PI * 2);
      if (p.kind === "SHIELD") {
        drawShield(ctx, p.w * 0.55);
      } else if (p.kind === "MULTIPLIER") {
        drawStar(ctx, p.w * 0.55, "#f0b323");
      } else {
        drawHeart(ctx, p.w * 0.55);
      }
      ctx.restore();
    }
  }

  private drawProjectiles(projectiles: Projectile[]): void {
    const { ctx } = this;
    for (const pr of projectiles) {
      const cx = pr.x + pr.w / 2;
      const cy = pr.y + pr.h / 2;
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate((cx / 24) % (Math.PI * 2));
      drawShield(ctx, pr.w * 0.55);
      ctx.restore();
    }
  }

  private drawPlayer(p: PlayerState, nowMs: number): void {
    const { ctx } = this;
    const invincible = nowMs < p.invincibleUntilMs;
    const multiplier = nowMs < p.multiplierUntilMs;
    const blink = invincible && Math.floor(nowMs / 90) % 2 === 0;
    if (blink) return;

    ctx.save();
    if (multiplier) {
      ctx.shadowColor = "#f0b323";
      ctx.shadowBlur = 14;
    }
    // Body — comic blue suit
    ctx.fillStyle = "#0a3161";
    roundedRect(ctx, p.x, p.y, p.w, p.h, 8);
    ctx.fill();

    // Chest star
    const cx = p.x + p.w / 2;
    const cy = p.y + p.h * 0.42;
    ctx.fillStyle = "#f8f9fa";
    ctx.beginPath();
    ctx.arc(cx, cy, p.w * 0.22, 0, Math.PI * 2);
    ctx.fill();
    ctx.save();
    ctx.translate(cx, cy);
    drawStar(ctx, p.w * 0.18, "#c8102e");
    ctx.restore();

    // Belt
    ctx.fillStyle = "#c8102e";
    ctx.fillRect(p.x, p.y + p.h * 0.6, p.w, 6);

    // Head + helmet (only when standing)
    if (!p.ducking) {
      ctx.fillStyle = "#0a3161";
      ctx.beginPath();
      ctx.arc(p.x + p.w / 2, p.y - 4, p.w * 0.32, Math.PI, 0);
      ctx.fill();
      ctx.fillStyle = "#fcd34d";
      ctx.fillRect(p.x + p.w * 0.25, p.y - 6, p.w * 0.5, 4);
    }
    ctx.restore();
  }

  private drawPauseOverlay(): void {
    const { ctx } = this;
    ctx.fillStyle = "rgba(6,18,43,0.78)";
    ctx.fillRect(0, 0, WORLD.WIDTH, WORLD.HEIGHT);
    ctx.fillStyle = "#f8f9fa";
    ctx.font = "bold 64px 'Bebas Neue', Impact, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("PAUSED", WORLD.WIDTH / 2, WORLD.HEIGHT / 2);
    ctx.font = "20px Inter, sans-serif";
    ctx.fillText("Press P to resume", WORLD.WIDTH / 2, WORLD.HEIGHT / 2 + 50);
  }
}

// ── Helpers ─────────────────────────────────────────────────────────────────
function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

function drawShield(ctx: CanvasRenderingContext2D, radius: number): void {
  // Concentric rings — captain's shield motif.
  const rings: Array<[string, number]> = [
    ["#c8102e", 1],
    ["#f8f9fa", 0.78],
    ["#c8102e", 0.6],
    ["#0a3161", 0.4],
  ];
  for (const [color, scale] of rings) {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(0, 0, radius * scale, 0, Math.PI * 2);
    ctx.fill();
  }
  // Center star
  drawStar(ctx, radius * 0.32, "#f8f9fa");
}

function drawStar(
  ctx: CanvasRenderingContext2D,
  radius: number,
  color: string
): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  for (let i = 0; i < 10; i++) {
    const r = i % 2 === 0 ? radius : radius * 0.45;
    const a = (Math.PI * 2 * i) / 10 - Math.PI / 2;
    const x = Math.cos(a) * r;
    const y = Math.sin(a) * r;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fill();
}

function drawHeart(ctx: CanvasRenderingContext2D, size: number): void {
  ctx.fillStyle = "#ef4444";
  ctx.beginPath();
  const s = size;
  ctx.moveTo(0, s * 0.3);
  ctx.bezierCurveTo(s, -s * 0.6, s * 1.4, s * 0.4, 0, s);
  ctx.bezierCurveTo(-s * 1.4, s * 0.4, -s, -s * 0.6, 0, s * 0.3);
  ctx.closePath();
  ctx.fill();
}

// ensure used in case obstacle constants aren't referenced elsewhere
void PLAYER;
